import { ReactNode, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Redirect } from "wouter";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import PwIndicator from "@/components/PwIndicator";
import authPlatformHero from "@/assets/auth/auth-platform-hero-branded.svg";
import memberPromoCustomer from "@/assets/auth/member-promo-customer.svg";
import memberCardProduct from "@/assets/auth/member-card-product.svg";

const loginSchema = z.object({
  identifier: z.string().min(1, "Email or username is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const passwordBase = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .refine((value) => /[a-z]/.test(value), {
    message: "Password must contain at least one lowercase letter",
  })
  .refine((value) => /[A-Z]/.test(value), {
    message: "Password must contain at least one uppercase letter",
  })
  .refine((value) => /\d/.test(value), {
    message: "Password must contain at least one number",
  })
  .refine((value) => /[@$!%*?&]/.test(value), {
    message: "Password must contain at least one special character (@$!%*?&)",
  });

const registerSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  email: z.string().email("Invalid email address"),
  password: passwordBase,
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
});

type AuthTab = "login" | "register";
type LoginAudience = "consumer" | "member";
type LoginFormValues = z.infer<typeof loginSchema>;
type RegisterFormValues = z.infer<typeof registerSchema>;

const platformBenefits = [
  "Real-time delivery tracking",
  "Priority store and service matching",
  "Secure integrated payments",
  "Member credits and saved profiles",
];

function getAuthTab(pathname: string, search: string): AuthTab {
  if (pathname === "/register") return "register";
  if (pathname === "/auth" && new URLSearchParams(search).get("tab") === "register") return "register";
  return "login";
}

export default function AuthPage() {
  const { user, login, register, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AuthTab>(() => getAuthTab(location.pathname, location.search));
  const [loginAudience, setLoginAudience] = useState<LoginAudience>("consumer");

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onSubmit",
    defaultValues: { identifier: "", password: "" },
  });

  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    mode: "onSubmit",
    defaultValues: {
      username: "",
      email: "",
      password: "",
      firstName: "",
      lastName: "",
    },
  });

  const newPassword = registerForm.watch("password") || "";

  useEffect(() => {
    setActiveTab(getAuthTab(location.pathname, location.search));
  }, [location.pathname, location.search]);

  const switchTab = (tab: AuthTab) => {
    setActiveTab(tab);
    navigate(tab === "login" ? "/auth?tab=login" : "/auth?tab=register");
  };

  const onLoginSubmit = async (data: LoginFormValues) => {
    const result = await login(data.identifier, data.password);

    if (!result.ok && result.code === "EMAIL_NOT_VERIFIED") {
      const verificationEmail = result.email || (data.identifier.includes("@") ? data.identifier : "");
      if (verificationEmail) sessionStorage.setItem("verificationEmail", verificationEmail);
      navigate(
        verificationEmail
          ? `/verify-email?email=${encodeURIComponent(verificationEmail)}`
          : "/verify-email",
      );
      return;
    }

    if (localStorage.getItem("token") !== null) {
      const redirectPath = sessionStorage.getItem("redirectAfterLogin");
      if (redirectPath) {
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath);
      } else {
        navigate(loginAudience === "member" ? "/membership/dashboard" : "/");
      }
    }
  };

  const onRegisterSubmit = async (data: RegisterFormValues) => {
    if (!sessionStorage.getItem("redirectAfterLogin")) {
      sessionStorage.setItem("redirectAfterLogin", "/account-setup");
    }
    await register(data);
    sessionStorage.setItem("verificationEmail", data.email);
    navigate(`/verify-email?email=${encodeURIComponent(data.email)}`);
  };

  if (user) return <Redirect to={loginAudience === "member" ? "/membership/dashboard" : "/"} />;

  const isRegister = activeTab === "register";

  return (
    <div className="relative isolate min-h-[calc(100svh-82px)] overflow-hidden bg-[#f5eee7] text-[#071229]">
      <img
        src={authPlatformHero}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[48%_center]"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.04)_0%,rgba(255,255,255,0.32)_32%,rgba(255,252,248,0.14)_63%,rgba(5,10,22,0.08)_100%)]" />

      <main className="relative z-10 mx-auto grid min-h-[calc(100svh-82px)] w-full max-w-[1500px] items-center gap-8 px-4 py-6 sm:px-8 lg:grid-cols-[minmax(390px,0.78fr)_minmax(480px,1.22fr)] lg:px-12 xl:gap-16 xl:px-20">
        <section
          aria-labelledby="auth-heading"
          className={`mx-auto w-full max-w-[440px] rounded-[28px] border border-white/80 bg-white/[0.94] p-5 shadow-[0_30px_90px_rgba(9,15,31,0.18)] backdrop-blur-xl sm:p-7 ${
            isRegister ? "lg:max-w-[500px]" : ""
          }`}
        >
          <div className="mb-5">
            <p className="mb-2 text-[11px] font-black uppercase tracking-[0.2em] text-[#f2551c]">
              Style.re membership
            </p>
            <h1 id="auth-heading" className="text-[2rem] font-semibold tracking-[-0.035em] text-[#070d1f]">
              {isRegister ? "Create your account" : "Welcome back"}
            </h1>
            <p className="mt-1.5 text-sm leading-6 text-[#6b7280]">
              {isRegister
                ? "Join Style.re and make every pickup feel effortless."
                : loginAudience === "member"
                  ? "Access your member account to continue."
                  : "Access your account to continue."}
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={(value) => switchTab(value as AuthTab)}>
            {!isRegister ? (
              <div
                className="mb-5 grid grid-cols-2 rounded-xl border border-[#ebe7e2] bg-[#f7f5f2] p-1"
                aria-label="Choose sign-in type"
              >
                {(["consumer", "member"] as const).map((audience) => (
                  <button
                    key={audience}
                    type="button"
                    onClick={() => setLoginAudience(audience)}
                    aria-pressed={loginAudience === audience}
                    className={`h-10 rounded-lg text-sm font-bold transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f] focus-visible:ring-offset-2 ${
                      loginAudience === audience
                        ? "bg-[#ff5a1f] text-white shadow-[0_8px_20px_rgba(255,90,31,0.22)]"
                        : "text-[#4b5563] hover:bg-white"
                    }`}
                  >
                    {audience === "consumer" ? "Consumer Sign In" : "Member Sign In"}
                  </button>
                ))}
              </div>
            ) : (
              <div
                className="mb-5 grid grid-cols-2 rounded-xl border border-[#ebe7e2] bg-[#f7f5f2] p-1 sm:grid-cols-3"
                aria-label="Choose account action"
              >
                <button
                  type="button"
                  onClick={() => {
                    setLoginAudience("consumer");
                    switchTab("login");
                  }}
                  className="h-10 min-w-0 whitespace-nowrap rounded-lg px-2 text-xs font-bold text-[#4b5563] transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f] focus-visible:ring-offset-2"
                >
                  Consumer Sign In
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLoginAudience("member");
                    switchTab("login");
                  }}
                  className="h-10 min-w-0 whitespace-nowrap rounded-lg px-2 text-xs font-bold text-[#4b5563] transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f] focus-visible:ring-offset-2"
                >
                  Member Sign In
                </button>
                <button
                  type="button"
                  aria-current="page"
                  className="col-span-2 h-10 min-w-0 whitespace-nowrap rounded-lg bg-[#ff5a1f] px-2 text-xs font-bold text-white shadow-[0_8px_20px_rgba(255,90,31,0.22)] sm:col-span-1"
                >
                  Create account
                </button>
              </div>
            )}

            <TabsContent value="login" className="mt-0">
              <Form {...loginForm}>
                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 w-full rounded-xl border-[#ddd9d4] bg-white font-semibold disabled:cursor-not-allowed disabled:opacity-100"
                    disabled
                    title="Google sign-in is not connected yet"
                  >
                    Continue with Google
                  </Button>

                  <FormField
                    control={loginForm.control}
                    name="identifier"
                    render={({ field }) => (
                      <AuthField label="Email or username" message={<FormMessage />}>
                        <Input
                          placeholder="your.email@example.com or username"
                          autoComplete="username"
                          className="h-11 rounded-xl border-[#ddd9d4] bg-white/90 px-4 focus-visible:ring-[#ff5a1f]"
                          {...field}
                        />
                      </AuthField>
                    )}
                  />

                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <AuthField label="Password" message={<FormMessage />}>
                        <Input
                          placeholder="Enter your password"
                          type="password"
                          autoComplete="current-password"
                          className="h-11 rounded-xl border-[#ddd9d4] bg-white/90 px-4 focus-visible:ring-[#ff5a1f]"
                          {...field}
                        />
                      </AuthField>
                    )}
                  />

                  <div className="-mt-1 text-right">
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-xs font-semibold text-[#f2551c] hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    className="h-11 w-full rounded-xl bg-[#ff5a1f] font-black text-white shadow-[0_12px_28px_rgba(255,90,31,0.2)] hover:bg-[#e84d14]"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : loginAudience === "member" ? (
                      "Member sign in"
                    ) : (
                      "Sign in"
                    )}
                  </Button>

                  <p className="text-center text-sm text-[#6b7280]">
                    New to Style.re?{" "}
                    <button type="button" onClick={() => switchTab("register")} className="font-bold text-[#f2551c] hover:underline">
                      Create an account
                    </button>
                  </p>
                </form>
              </Form>
            </TabsContent>

            <TabsContent value="register" className="mt-0">
              <Form {...registerForm}>
                <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-3.5">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 w-full rounded-xl border-[#ddd9d4] bg-white font-semibold disabled:cursor-not-allowed disabled:opacity-100"
                    disabled
                    title="Google sign-up is not connected yet"
                  >
                    Continue with Google
                  </Button>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <FormField
                      control={registerForm.control}
                      name="firstName"
                      render={({ field }) => (
                        <AuthField label="First name" message={<FormMessage />}>
                          <Input
                            placeholder="Jordan"
                            autoComplete="given-name"
                            className="h-10 rounded-xl border-[#ddd9d4] bg-white/90 px-3 focus-visible:ring-[#ff5a1f]"
                            {...field}
                          />
                        </AuthField>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="lastName"
                      render={({ field }) => (
                        <AuthField label="Last name" message={<FormMessage />}>
                          <Input
                            placeholder="Davis"
                            autoComplete="family-name"
                            className="h-10 rounded-xl border-[#ddd9d4] bg-white/90 px-3 focus-visible:ring-[#ff5a1f]"
                            {...field}
                          />
                        </AuthField>
                      )}
                    />
                  </div>

                  <FormField
                    control={registerForm.control}
                    name="username"
                    render={({ field }) => (
                      <AuthField label="Username" message={<FormMessage />}>
                        <Input
                          placeholder="jordandavis"
                          autoComplete="username"
                          className="h-10 rounded-xl border-[#ddd9d4] bg-white/90 px-3 focus-visible:ring-[#ff5a1f]"
                          {...field}
                        />
                      </AuthField>
                    )}
                  />

                  <FormField
                    control={registerForm.control}
                    name="email"
                    render={({ field }) => (
                      <AuthField label="Email" message={<FormMessage />}>
                        <Input
                          placeholder="jordan@example.com"
                          type="email"
                          autoComplete="email"
                          className="h-10 rounded-xl border-[#ddd9d4] bg-white/90 px-3 focus-visible:ring-[#ff5a1f]"
                          {...field}
                        />
                      </AuthField>
                    )}
                  />

                  <FormField
                    control={registerForm.control}
                    name="password"
                    render={({ field }) => (
                      <AuthField label="Password" message={<FormMessage />}>
                        <Input
                          placeholder="Create a secure password"
                          type="password"
                          autoComplete="new-password"
                          className="h-10 rounded-xl border-[#ddd9d4] bg-white/90 px-3 focus-visible:ring-[#ff5a1f]"
                          {...field}
                        />
                      </AuthField>
                    )}
                  />

                  {newPassword && <PwIndicator password={newPassword} />}

                  <Button
                    type="submit"
                    className="h-11 w-full rounded-xl bg-[#ff5a1f] font-black text-white shadow-[0_12px_28px_rgba(255,90,31,0.2)] hover:bg-[#e84d14]"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create my account"
                    )}
                  </Button>

                  <p className="text-center text-xs leading-5 text-[#6b7280]">
                    By continuing, you agree to our{" "}
                    <Link to="/terms-and-conditions" className="font-semibold text-[#071229] underline underline-offset-2">Terms</Link>
                    {" "}and{" "}
                    <Link to="/privacy-policy" className="font-semibold text-[#071229] underline underline-offset-2">Privacy Policy</Link>.
                  </p>
                </form>
              </Form>
            </TabsContent>
          </Tabs>

          <Link
            to="/membership/choose-lane"
            className="mt-5 grid grid-cols-[1fr_auto] items-center gap-4 rounded-2xl border border-[#f2d7c6] bg-[linear-gradient(120deg,#fff7f0_0%,#fffdf9_100%)] p-4 transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f]"
          >
            <span>
              <span className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#f2551c]">Membership</span>
              <span className="mt-1 block text-sm font-black text-[#071229]">Unlock priority pickup and member credits</span>
              <span className="mt-1 flex items-center gap-1 text-xs font-bold text-[#f2551c]">Explore benefits <ArrowRight className="h-3.5 w-3.5" /></span>
            </span>
            <img
              src={memberPromoCustomer}
              alt=""
              className="h-24 w-20 rounded-xl object-cover object-center shadow-[0_14px_25px_rgba(0,0,0,0.18)]"
            />
          </Link>
        </section>

        <section className="hidden max-w-[580px] justify-self-center lg:block">
          <p className="text-xs font-black uppercase tracking-[0.26em] text-[#f2551c]">Delivering style, seamlessly</p>
          <h2 className="mt-5 font-serif text-[3.75rem] font-black leading-[0.93] tracking-[-0.045em] text-[#071229] drop-shadow-[0_2px_12px_rgba(255,255,255,0.8)]">
            The Style.re
            <br />
            Delivery Platform
          </h2>
          <p className="mt-6 max-w-md text-lg leading-7 text-[#2d3748]">
            Priority retail pickup for members who value time, service, and a beautifully simple experience.
          </p>

          <ul className="mt-8 grid gap-4">
            {platformBenefits.map((benefit) => (
              <li key={benefit} className="flex items-center gap-4 text-[15px] font-semibold text-[#172033]">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-[#f2551c] shadow-sm backdrop-blur">
                  <Check className="h-4 w-4" strokeWidth={2.5} />
                </span>
                {benefit}
              </li>
            ))}
          </ul>

          <Link
            to="/membership/choose-lane"
            aria-label="Explore Style.re membership"
            className="relative z-20 mt-9 grid max-w-[440px] cursor-pointer grid-cols-[132px_1fr_auto] items-center gap-4 rounded-[22px] border border-white/80 bg-white/[0.9] p-3 shadow-[0_22px_60px_rgba(7,13,36,0.16)] backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f]"
          >
            <img
              src={memberCardProduct}
              alt=""
              className="h-[86px] w-[132px] rounded-2xl object-cover shadow-[0_12px_28px_rgba(0,0,0,0.24)]"
            />
            <span>
              <span className="text-[10px] font-black uppercase tracking-[0.22em] text-[#f2551c]">Style.re membership</span>
              <span className="mt-1 block font-serif text-xl font-black leading-5 text-[#071229]">More than delivery. It&apos;s a lifestyle.</span>
            </span>
            <ArrowRight className="h-5 w-5 text-[#f2551c]" />
          </Link>
        </section>
      </main>
    </div>
  );
}

function AuthField({
  label,
  children,
  message,
}: {
  label: string;
  children: ReactNode;
  message: ReactNode;
}) {
  return (
    <FormItem className="space-y-1.5">
      <FormLabel className="text-xs font-bold text-[#1f2937]">{label}</FormLabel>
      <FormControl>{children}</FormControl>
      {message}
    </FormItem>
  );
}

